'use client';

import React from 'react';
import { Box, Flex, HStack, Text } from '@skin-seoul/design-system';
import { formatCurrency, useCartService, useRegionCurrency, type Country } from '@skin-seoul/service';
import { BMSMGradient } from './components/BMSMGradient';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { thresholdsPerCountry } from './constants';

export type BMSMCampaignState =
  | 'Empty cart'
  | 'Less than free shipping'
  | 'Free shipping available'
  | 'Less than 10% OFF'
  | '10% OFF available';

interface BMSMCampaignBannerProps {
  /** Override automatic state calculation with manual state */
  state?: BMSMCampaignState;
  className?: string;
}

export function BMSMCampaignBanner({ state: manualState, className = '' }: BMSMCampaignBannerProps) {
  const { country, currency } = useRegionCurrency();
  const { useGetCart } = useCartService();

  // Get cart data
  const { data: cartData } = useGetCart({ currency });

  // Get thresholds for current country
  const thresholds = thresholdsPerCountry[country as Country];
  const cartTotal = (cartData?.cart.totals.total ?? 0) * 0.01;

  // Calculate campaign state based on cart total
  const calculateState = (): BMSMCampaignState => {
    if (manualState) {
      return manualState;
    }

    if (cartTotal === 0) {
      return 'Empty cart';
    }
    if (cartTotal < thresholds.freeShipping) {
      return 'Less than free shipping';
    }
    if (cartTotal >= thresholds.freeShipping && cartTotal < thresholds.tenPercentOff) {
      return 'Free shipping available';
    }
    if (cartTotal >= thresholds.tenPercentOff) {
      return '10% OFF available';
    }
    return 'Less than 10% OFF';
  };

  const state = calculateState();

  const getProgressValue = () => {
    if (cartTotal === 0) {
      return 0;
    }
    if (cartTotal < thresholds.freeShipping) {
      return (cartTotal / thresholds.freeShipping) * 20;
    }
    if (cartTotal < thresholds.tenPercentOff) {
      return 20 + ((cartTotal - thresholds.freeShipping) / (thresholds.tenPercentOff - thresholds.freeShipping)) * 60;
    }
    // 10% OFF 달성 시 100% 표시
    return 100;
  };

  const getFreeShippingStatus = () => {
    return cartTotal >= thresholds.freeShipping ? 'achieved' : 'pending';
  };

  const getDiscountStatus = () => {
    return cartTotal >= thresholds.tenPercentOff ? 'achieved' : 'pending';
  };

  const getDiscountText = () => {
    if (cartTotal >= thresholds.tenPercentOff) {
      return 'Available';
    }
    const remaining = thresholds.tenPercentOff - cartTotal;
    return `Spend ${formatCurrency(currency, remaining)}`;
  };

  const getFreeShippingText = () => {
    if (cartTotal >= thresholds.freeShipping) {
      return 'Available';
    }
    const remaining = thresholds.freeShipping - cartTotal;
    return `Spend ${formatCurrency(currency, remaining)}`;
  };

  const showFreeShippingConfetti = cartTotal >= thresholds.freeShipping;

  const showDiscountConfetti = cartTotal >= thresholds.tenPercentOff;

  return (
    <Flex
      position="relative"
      width="100%"
      direction="column"
      bg="generic.100"
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
      py="20px"
      minH="150px"
      height="150px"
      gap={2}
      borderRadius="16px"
      className={className}
      data-name={`State=${state}`}
    >
      {/* Background Gradient */}
      <BMSMGradient />

      <HStack px="48px" gap="32px" justify="space-around">
        <Flex direction="column" gap="2px">
          <Text
            textStyle="label.small"
            fontWeight="medium"
            color={getFreeShippingStatus() === 'achieved' ? 'primary.200' : 'neutral.400'}
          >
            {getFreeShippingText()}
          </Text>
          <Text textStyle="label.large" fontWeight="bold" color="generic.0" letterSpacing="-0.64px">
            Free Shipping
          </Text>
        </Flex>
        <Box width="1px" height="38px" borderRight="1px dotted" borderColor="neutral.600" />

        <Flex direction="column" gap="2px" align="center">
          <Text
            textStyle="label.small"
            fontWeight="medium"
            color={getDiscountStatus() === 'achieved' ? 'primary.200' : 'neutral.400'}
          >
            {getDiscountText()}
          </Text>
          <Text textStyle="label.large" fontWeight="bold" color="generic.0" letterSpacing="-0.64px">
            10% OFF
          </Text>
        </Flex>
      </HStack>
      <Flex position="relative" width="100%" height="24px" alignItems="center" justifyContent="center">
        <Box position="relative" width="278px" height="4px" bg="neutral.600" borderRadius="2px">
          <Box
            width={`${getProgressValue()}%`}
            height="100%"
            bg="success.600"
            borderRadius="2px"
            transition="width 0.3s ease"
          />
          {/* Free Shipping Checkpoint - 20% position */}
          <Box zIndex={1} position="absolute" left="44px" top="-12px" width="24px" height="24px">
            <CheckIcon achieved={getFreeShippingStatus() === 'achieved'} />
          </Box>

          {/* 10% OFF Checkpoint - 80% position */}
          <Box zIndex={1} position="absolute" left="210px" top="-12px" width="24px" height="24px">
            <CheckIcon achieved={getDiscountStatus() === 'achieved'} />
          </Box>
        </Box>
      </Flex>

      <ConfettiAnimation
        showFreeShippingConfetti={showFreeShippingConfetti}
        showDiscountConfetti={showDiscountConfetti}
      />

      <Flex align="center" gap="2px">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none">
          <path d="M21 1H1" stroke="url(#paint0_linear_2188_5477)" strokeLinecap="round" />
          <defs>
            <linearGradient id="paint0_linear_2188_5477" x1="21" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse">
              <stop stopColor="white" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        <Text textStyle="label.xsmall" fontWeight="medium" color="generic.0" whiteSpace="pre">
          New here? Free shipping with no min spend!
        </Text>

        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none">
          <path d="M1 1H21" stroke="url(#paint0_linear_2188_5479)" strokeLinecap="round" />
          <defs>
            <linearGradient id="paint0_linear_2188_5479" x1="1" y1="2" x2="21" y2="2" gradientUnits="userSpaceOnUse">
              <stop stopColor="white" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </Flex>
    </Flex>
  );
}
